import { useId, type ReactNode } from 'react';
import { cn } from '../utils/cn';

export interface ToggleProps {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  /**
   * The switch's name — rendered as visible text *and* used as the accessible
   * name, because it is the same node.
   *
   * Required, not optional. It used to be optional and was passed only to
   * `aria-label`, so every toggle in every consuming app read correctly to a
   * screen reader and appeared as a bare unexplained switch to everyone else.
   * A toggle without a name is the defect, so the type no longer allows one.
   */
  label: string;
  /**
   * Secondary line below the switch — consequences, caveats, what to do
   * instead. Exposed as the switch's description rather than part of its name,
   * so assistive tech announces "Verify TLS certificates, switch, on" and
   * offers the explanation separately instead of reading a paragraph as a name.
   */
  hint?: ReactNode;
  className?: string;
}

/**
 * An on/off switch.
 *
 * The whole control — track and text — is one `<button role="switch">`, so the
 * accessible name comes from the rendered text rather than a parallel
 * `aria-label` that can silently drift from it, and the label is part of the
 * hit target the way a native checkbox label is.
 *
 * Tone is deliberately not a prop. Whether a given switch being off is
 * dangerous is the consumer's knowledge, not the library's — pass a `hint`
 * that says so.
 */
export function Toggle({ checked, onChange, disabled = false, label, hint, className }: ToggleProps) {
  const hintId = useId();

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-describedby={hint ? hintId : undefined}
        disabled={disabled}
        onClick={onChange}
        className={cn(
          'inline-flex items-center gap-3 text-left',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        )}
      >
        <span
          className={cn(
            'relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors',
            checked ? 'bg-action-primary' : 'bg-bg-subtle',
          )}
        >
          <span
            className={cn(
              'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition-transform',
              checked ? 'translate-x-5' : 'translate-x-0',
            )}
          />
        </span>
        <span className="text-sm text-text-primary">{label}</span>
      </button>
      {hint && (
        <p id={hintId} className="text-sm text-text-muted">
          {hint}
        </p>
      )}
    </div>
  );
}
